import React from "react";
import { ChromePicker } from "react-color";
import { Box, Grid, makeStyles, Paper } from "@material-ui/core";
import {
  KCropperWrapper,
  KSelectAutocomplete,
  KSwitch,
  KTextInput,
} from "@kyburz/kdesign-system";
import { languages } from "../../../components/constants/languages";
import { countries } from "../../../components/constants/countries";
import { getAuthUploadUrl } from "../../../apiCalls/imageUploads/authUpload";
import useSetState from "../../../hooks/useSetState";
import { humanize } from "../../../utils/utils";
import InternalBillingSettings from "./InternalBilling/InternalBillingSettings";
import CopySettings from "./CopySettings/CopySettings";

interface Props {
  handleSelectChange: (v?, v1?) => void;
  handleTextInputChange: (v?, v1?) => void;
  handleColorChange: (v?, v1?) => void;
  handleToggle: (v?) => void;
  setLogo: (v?, v1?) => void;
  company: string;
  name: string;
  logo: string;
  shortname: string;
  language: string;
  timezone: string;
  street: string;
  city: string;
  zip: string;
  country: string;
  color: string;
  archived: boolean;
  internals: Record<string, any>;
  handleInternalsChange: (v?) => void;
}

const General: React.FC<Props> = ({
  handleTextInputChange,
  handleSelectChange,
  handleColorChange,
  handleToggle,
  setLogo,
  company,
  name,
  logo,
  shortname,
  language,
  timezone,
  street,
  city,
  zip,
  country,
  color,
  archived,
  internals,
  handleInternalsChange,
}: Props) => {
  const useStyles = makeStyles((theme) => ({
    root: {
      paddingBottom: 100,
    },
    header: {
      fontSize: 20,
      fontWeight: 500,
      color: theme.palette.common.black,
      width: "100%",
      paddingBottom: 16,
      borderBottom: "1px solid #e8e8e8",
    },
    paper: {
      padding: 32,
      boxShadow: "none",
    },
    colorPicker: { display: "flex" },
    colorBlock: {
      background: color || "#fff",
      height: 40,
      width: 40,
      alignSelf: "flex-end",
      marginLeft: 8,
      borderRadius: 4,
      cursor: "pointer",
      border: `1px solid ${color || "#bdbdbd"}`,
    },
  }));
  const classes = useStyles();
  const [{ showColorPicker }, setState] = useSetState({
    showColorPicker: false,
  });
  const fields = [
    {
      name: "name",
      value: name,
      label: "Company Name",
      style: { display: "inline-block", marginRight: 16 },
      onChange: handleTextInputChange,
    },
    {
      name: "shortname",
      value: shortname,
      label: "Short Name",
      disabled: true,
      style: { display: "inline-block" },
    },
    {
      name: "language",
      value: humanize(language),
      label: "Language",
      style: { display: "inline-block", marginRight: 16, width: 240 },
      type: "select",
      elements: languages,
      onChange: (v, v1) => {
        handleSelectChange(v, { value: v1.label });
      },
    },
    {
      name: "timezone",
      value: timezone,
      label: "Timezone",
      disabled: true,
      style: { display: "inline-block" },
    },
    {
      name: "street",
      value: street,
      label: "Street",
      placeholder: "Shedweg 2/8",
      width: "100%",
      onChange: handleTextInputChange,
    },
    {
      name: "city",
      value: city,
      label: "City",
      placeholder: "Freienstein",
      style: { display: "inline-block", marginRight: 16 },
      onChange: handleTextInputChange,
    },
    {
      name: "zip",
      value: zip,
      label: "Zip",
      placeholder: "8427",
      style: { display: "inline-block" },
      onChange: handleTextInputChange,
    },
    {
      name: "country",
      value: countries?.find((x) => x.label === country || x.name === country)
        ?.name,
      label: "Country",
      type: "select",
      style: { width: "100%" },
      elements: countries?.map((x) => ({
        label: x?.name,
      })),
      onChange: (v, v1) => {
        handleSelectChange(v, { value: v1.label });
      },
    },
  ];

  return (
    <div className={classes.root}>
      <Grid container spacing={6}>
        <Grid item xs={6}>
          <Paper className={classes.paper}>
            <div className={classes.header}>Company Information</div>
            {fields.map((x) => {
              return x.type === "select" ? (
                <KSelectAutocomplete
                  key={x.name}
                  placeholder="Value"
                  name={x.name}
                  elements={x.elements}
                  onChange={x.onChange}
                  value={x.value}
                  label={x.label}
                  style={x.style}
                />
              ) : (
                <KTextInput
                  key={x.name}
                  onChange={handleTextInputChange}
                  value={x.value}
                  disabled={x.disabled}
                  label={x.label}
                  style={x.style}
                  placeholder={x.placeholder}
                  name={x.name}
                  width={x.width}
                />
              );
            })}
            <KSwitch
              name="archiveCompany"
              onChange={() =>
                handleToggle({ name: "archived", value: archived })
              }
              checked={archived}
              label="Archive company"
              switchCommands={{ on: "Active", off: "Archived" }}
            />
          </Paper>
          <Box mt={4}>
            <CopySettings />
          </Box>
        </Grid>
        <Grid item xs={6}>
          <Paper className={classes.paper}>
            <div className={classes.header}>Company Visuals</div>
            <div style={{ marginTop: 52 }}>
              <KCropperWrapper
                helperText="Allowed files are .PNG, .JPG and .JPEG"
                uploadPosition="right"
                width={430}
                height={128}
                borderRadius={0}
                setAvatar={setLogo}
                fetchUrl={getAuthUploadUrl}
                uploadUrl={`companies/${company}/upload_logo`}
                image={logo}
              />
            </div>
            <div className={classes.colorPicker}>
              <KTextInput
                label="Company Color"
                value={color}
                onChange={() => {}}
                disabled
                style={{ width: 240 }}
              />
              <div
                className={classes.colorBlock}
                onClick={() => setState({ showColorPicker: !showColorPicker })}
              />
            </div>
            <div
              style={{
                position: "absolute",
                display: showColorPicker ? "flex" : "none",
                justifyContent: "center",
                marginTop: 8,
              }}
            >
              <ChromePicker
                onChangeComplete={handleColorChange}
                color={color || "#fff"}
              />
            </div>
          </Paper>
          <Paper className={classes.paper} style={{ marginTop: 20 }}>
            <div className={classes.header}>Internal Billing</div>
            <InternalBillingSettings
              internals={internals}
              setInternals={handleInternalsChange}
            />
          </Paper>
        </Grid>
      </Grid>
    </div>
  );
};

export default General;
